import { ExternalLink } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { assetUrl, encodeUrl } from '../../../common/helpers';
import { useAddParticipantFieldImportModal } from './AddParticipantFieldImportForm';
import GetParticipantFieldImportsQuery from './GetParticipantFieldImportsQuery';
import NoEventsCard from '../NoEventsCard';
import ParticipantFieldImportCard from './ParticipantFieldImportCard';
import UI from '../../../common/UI';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useUrlState from '../../../common/useUrlState';

export interface ParticipantFieldImportsPageProps {
  participantFieldImportId?: string;
}

const ParticipantFieldImportsPage = ({ participantFieldImportId }: ParticipantFieldImportsPageProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const history = useHistory();

  const [urlState] = useUrlState({
    parse: (rawParams, { string, integer }) => ({
      ...rawParams,
      event: string(rawParams.event),
      page: integer(rawParams.page, 1),
    }),
  });

  const { data } = useQuery(
    GetParticipantFieldImportsQuery,
    {
      variables: {
        page: urlState.page,
        per_page: 15,
        project_id: project.id,
      },
      pollInterval: 5000,
    },
  );

  const [openAddImportModal] = useAddParticipantFieldImportModal({
    eventId: urlState.event,
    onSuccess: (participantFieldImport) => {
      // Forget the current page, the new import will appear at the top of the first page
      const queryParams = encodeUrl({ ...urlState, page: null });
      history.push(`/${project.id}/participants/imports/${participantFieldImport.id}?${queryParams}`);
    },
  });

  const handleDelete = () => {
    // Remember the query params
    const queryParams = encodeUrl(urlState);
    history.push(`/${project.id}/participants/imports?${queryParams}`);
  };

  if (!data) {
    return <UI.PageLoader />;
  }

  const participantFieldImports = data.project.participant_field_imports;

  if (data.project.events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.SectionHeading>
            {t('imports')}
          </UI.SectionHeading>
          {participantFieldImports.total > 0 && (
            <UI.Button
              onClick={() => openAddImportModal()}
              sc={{ brand: 'secondary' }}
            >
              {t('add_import')}
            </UI.Button>
          )}
        </UI.FlexContainer>

        {participantFieldImports.total > 0 && (
          <UI.GridContainer sc={{ gutter: 0.5 }}>
            {participantFieldImports.data.map((participantFieldImport) => (
              <ParticipantFieldImportCard
                participantFieldImport={participantFieldImport}
                active={participantFieldImportId === participantFieldImport.id}
                muted={participantFieldImportId && participantFieldImportId !== participantFieldImport.id}
                onDelete={handleDelete}
                key={participantFieldImport.id}
              />
            ))}
          </UI.GridContainer>
        )}

        {participantFieldImports.last_page > 1 && (
          <UI.Div>
            <UI.Pager pageCount={participantFieldImports.last_page} scroll />
          </UI.Div>
        )}

        {participantFieldImports.total === 0 && (
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
            <img src={assetUrl('images/participant.svg')} alt="" height={120} />
            <UI.H3 sc={{ mb: 2 }}>{t('no_participant_field_imports_yet')}</UI.H3>
            <UI.Paragraph sc={{ mb: 1 }}>
              {t('no_participant_field_imports_get_started')}
            </UI.Paragraph>
            <UI.Paragraph sc={{ mb: 1 }}>
              <UI.A href="https://atleta.cc/article/import-participants" target="_blank" rel="noopener noreferer">
                {t('how_to_import_participants')}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.A>
            </UI.Paragraph>
            <UI.Paragraph>
              <UI.A href="https://atleta.cc/article/import-custom-fields" target="_blank" rel="noopener noreferer">
                {t('how_to_import_participant_field_entries')}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.A>
            </UI.Paragraph>
            <UI.Button
              onClick={() => openAddImportModal()}
              sc={{ brand: 'secondary' }}
            >
              {t('add_an_import')}
            </UI.Button>
          </UI.Card>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

export default ParticipantFieldImportsPage;
